import { Plus, Power, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { DAYS } from "@/lib/format";
import type { PowerConfig, PowerSlot } from "@/lib/types";

export function PowerScheduleEditor({ value, onChange, disabled }: { value: PowerConfig; onChange: (p: PowerConfig) => void; disabled?: boolean }) {
  const setSlot = (i: number, s: PowerSlot) => onChange({ ...value, slots: value.slots.map((x, j) => (j === i ? s : x)) });
  const toggleDay = (i: number, d: number) => {
    const s = value.slots[i];
    const days = s.days.includes(d) ? s.days.filter((x) => x !== d) : [...s.days, d].sort();
    if (days.length) setSlot(i, { ...s, days });
  };
  const add = () => onChange({ ...value, slots: [...value.slots, { days: [1, 2, 3, 4, 5], on_time: "08:00", off_time: "22:00" }] });

  return (
    <div className="space-y-4" data-testid="power-schedule-editor">
      <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-950/40 px-4 py-3">
        <div className="flex items-start gap-3">
          <Power className={`mt-0.5 h-4 w-4 ${value.enabled ? "text-emerald-400" : "text-slate-500"}`} />
          <div><p className="text-sm font-medium text-slate-200">Allumage / extinction automatique</p><p className="text-xs text-slate-500">La télé s'éteint en dehors des plages d'allumage ci-dessous (fuseau horaire de l'écran).</p></div>
        </div>
        <Switch disabled={disabled} data-testid="power-enabled-switch" checked={value.enabled} onCheckedChange={(c) => onChange({ ...value, enabled: c, slots: c && !value.slots.length ? [{ days: [1, 2, 3, 4, 5, 6, 7], on_time: "08:00", off_time: "22:00" }] : value.slots })} />
      </div>
      {value.enabled && (
        <>
          {value.slots.map((s, i) => (
            <div key={i} data-testid={`power-slot-${i}`} className="grid gap-3 rounded-lg border border-slate-800 bg-slate-950/40 p-4 lg:grid-cols-[2fr_auto_auto_auto] lg:items-end">
              <div>
                <Label className="eyebrow">Jours</Label>
                <div className="mt-1 flex flex-wrap gap-1">
                  {DAYS.map((d) => (
                    <button key={d.v} type="button" disabled={disabled} data-testid={`power-slot-${i}-day-${d.v}`} onClick={() => toggleDay(i, d.v)}
                      className={`h-9 w-11 rounded-md border text-xs font-semibold transition-colors ${s.days.includes(d.v) ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300" : "border-slate-700 text-slate-500 hover:text-slate-300"}`}>{d.short}</button>
                  ))}
                </div>
              </div>
              <div><Label className="eyebrow">Allumer à</Label><Input type="time" disabled={disabled} data-testid={`power-slot-${i}-on`} value={s.on_time} onChange={(e) => setSlot(i, { ...s, on_time: e.target.value })} className="border-slate-700 bg-slate-950/60" /></div>
              <div><Label className="eyebrow">Éteindre à</Label><Input type="time" disabled={disabled} data-testid={`power-slot-${i}-off`} value={s.off_time} onChange={(e) => setSlot(i, { ...s, off_time: e.target.value })} className="border-slate-700 bg-slate-950/60" /></div>
              <Button type="button" variant="ghost" size="icon" disabled={disabled} data-testid={`power-slot-${i}-remove`} onClick={() => onChange({ ...value, slots: value.slots.filter((_, j) => j !== i) })} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          {!value.slots.length && <p className="rounded-lg border border-dashed border-amber-500/40 p-4 text-sm text-amber-300">Aucune plage d'allumage : la télé restera éteinte en permanence.</p>}
          <Button type="button" variant="outline" size="sm" disabled={disabled} data-testid="power-add-slot" onClick={add} className="border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700"><Plus className="mr-1 h-4 w-4" />Ajouter une plage d'allumage</Button>
        </>
      )}
    </div>
  );
}
